import {
  Dialog,
  DialogContent,
  DialogActions,
  DialogTitle,
  Typography,
  Button,
  Backdrop,
  useMediaQuery,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';

import React from 'react';

/**
 * Component representing an informative dialog.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Object} props.informativeDialog - Information about the informative dialog.
 * @param {boolean} props.informativeDialog.isOpen - Determines whether the dialog is open or closed.
 * @param {string} props.informativeDialog.width - The width of the dialog.
 * @param {string} props.informativeDialog.title - The title of the dialog.
 * @param {string} props.informativeDialog.subTitle - The subtitle or content of the dialog.
 * @param {Function} props.setInformativeDialog - Function to set the state of the informative dialog.
 * @returns {JSX.Element} The InformativeDialog component.
 */
const InformativeDialog = props => {
  const {informativeDialog, setInformativeDialog} = props;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme => theme.breakpoints.down('sm'));

  const dialogStyle = {
    '& .MuiPaper-root': {
      borderRadius: '20px',
    },
    top: theme.spacing(5),
    paddingY: theme.spacing(2),
  };

  return (
    <Dialog
      open={informativeDialog.isOpen}
      maxWidth={informativeDialog.width}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{
        timeout: 500,
      }}
      PaperProps={{
        sx: {
          position: 'absolute',
          mx: 'auto',
        },
      }}
      sx={dialogStyle}
    >
      <DialogTitle
        sx={{
          paddingTop: theme.spacing(6),
          pb: theme.spacing(1),
          textAlign: 'center',
        }}
      >
        <Typography component="span" variant="h5" fontWeight={'bold'}>
          {informativeDialog.title}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{paddingY: theme.spacing(1), textAlign: 'center'}}>
        <Typography variant="subtitle1">{informativeDialog.subTitle}</Typography>
      </DialogContent>
      <DialogActions
        sx={{
          paddingBottom: theme.spacing(2),
          paddingX: isSmallScreen ? theme.spacing(3.8) : theme.spacing(1.7),
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 9,
        }}
      >
        <Button variant="text" onClick={informativeDialog.onConfirm}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default InformativeDialog;
